import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type SpotifyTrack, type SpotifySearchResults, type SpotifyDevice } from '@/lib/api';
import { Search, Play, X, Music2 } from 'lucide-react';

interface SpotifySearchProps {
  /** Name of the Sonos group currently selected. Used to find a matching Spotify Connect device. */
  selectedGroupName?: string | null;
  onClose: () => void;
}

// Debounce a value by ms — saves API calls while user is still typing.
function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function SpotifySearch({ selectedGroupName, onClose }: SpotifySearchProps) {
  const qc = useQueryClient();
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounced(query.trim(), 350);

  const search = useQuery<SpotifySearchResults>({
    queryKey: ['spotify', 'search', debouncedQuery],
    queryFn: () => api.searchSpotify(debouncedQuery),
    enabled: debouncedQuery.length >= 2,
    staleTime: 60_000, // 1 min — search results don't change much
  });

  // Need Spotify device list to map the user's selected Sonos room → Spotify
  // device id. The Sonos and Spotify naming can differ ("Kitchen" vs
  // "Kitchen/Dining"); we match on substring containment (case-insensitive)
  // either way. If no match, fall back to the active Spotify device.
  const devices = useQuery<SpotifyDevice[]>({
    queryKey: ['spotify', 'devices'],
    queryFn: () => api.listSpotifyDevices(),
    staleTime: 30_000,
  });

  function findSpotifyDeviceId(): { deviceId?: string; warning?: string } {
    if (!devices.data || !selectedGroupName) return {};
    const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
    const target = norm(selectedGroupName);
    // EXACT match only. Substring matching is dangerous because Stu has
    // overlapping device names — a Sonos "Kitchen/Dining" AVR and an Amazon
    // Echo "Kitchen" are different physical speakers. Substring would happily
    // pick the wrong one (music plays on Echo instead of Sonos) and the user
    // would have no idea which speaker took the command.
    const match = devices.data.find((d) => norm(d.name) === target);
    if (match && match.id) return { deviceId: match.id };
    // No exact match — refuse to play on a wrong device. Surface a clear
    // explainer so the user can pick a room that IS in Spotify Connect, or
    // use the Sonos app to play directly to the Sonos-only group.
    return {
      warning: `${selectedGroupName} isn't a Spotify Connect target (probably a Sonos AVR or a Sonos-only group). Pick a different room from the picker, or use the Sonos app to play music on ${selectedGroupName} directly.`,
    };
  }

  const play = useMutation({
    mutationFn: (track: SpotifyTrack) => {
      const { deviceId, warning } = findSpotifyDeviceId();
      if (!deviceId) {
        // Refuse — better to fail loudly than play on the wrong speaker.
        throw new Error(warning ?? "Can't find a Spotify-controllable speaker for this room.");
      }
      return api.spotifyControl('play', { uris: [track.uri], deviceId });
    },
    onSuccess: () => {
      // Re-poll Sonos and Spotify state right away so the modal reflects.
      qc.invalidateQueries({ queryKey: ['sonos', 'state'] });
      qc.invalidateQueries({ queryKey: ['spotify', 'now-playing'] });
    },
  });

  const deviceInfo = findSpotifyDeviceId();
  const playError = play.error instanceof Error ? play.error.message : null;
  const tracks = search.data?.tracks ?? [];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <Search size={14} className="opacity-60" />
        <input
          type="text"
          autoFocus
          placeholder="Search Spotify… songs, artists, albums"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="flex-1 bg-white/5 border border-white/10 rounded-md px-3 py-1.5 text-[13px] text-white placeholder:text-white/40 focus:outline-none focus:border-rose-300/50"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery('')}
            aria-label="Clear search"
            className="p-1 rounded hover:bg-white/10"
          >
            <X size={12} />
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          className="text-[10px] tracking-[0.14em] uppercase opacity-60 hover:opacity-100"
        >
          Done
        </button>
      </div>

      {(playError || deviceInfo.warning) && (
        <div className="text-[10px] leading-snug px-2 py-1.5 rounded bg-rose-200/15 border border-rose-200/25 text-rose-50/90">
          {playError ?? deviceInfo.warning}
        </div>
      )}

      {debouncedQuery.length < 2 ? (
        <p className="text-[11px] opacity-50 italic px-1">Type at least two letters.</p>
      ) : search.isLoading ? (
        <p className="text-[11px] opacity-50 px-1">Searching…</p>
      ) : tracks.length === 0 ? (
        <p className="text-[11px] opacity-50 italic px-1">No matches.</p>
      ) : (
        <div className="flex flex-col gap-1 max-h-[280px] overflow-y-auto pr-1">
          {tracks.map((t) => (
            <button
              key={t.uri}
              type="button"
              onClick={() => play.mutate(t)}
              disabled={play.isPending}
              className="flex items-center gap-3 px-2 py-1.5 rounded-md bg-white/[0.03] hover:bg-white/[0.08] disabled:opacity-50 text-left transition-colors group"
            >
              {t.artUrl ? (
                <img
                  src={t.artUrl}
                  alt=""
                  className="w-10 h-10 rounded-sm object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-10 h-10 rounded-sm bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Music2 size={14} className="opacity-50" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-[13px] truncate">{t.name}</div>
                <div className="text-[10px] opacity-60 truncate">
                  {t.artists}
                  {t.album ? ` · ${t.album}` : ''}
                </div>
              </div>
              <Play
                size={14}
                className="flex-shrink-0 opacity-40 group-hover:opacity-100"
                fill="currentColor"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
